import { useTranslation } from 'react-i18next';

import { formatDate } from '../i18n.js';

/** Colour of a 0–100 score; the number is always shown as text too (never colour alone). */
const tone = (score: number) =>
  score >= 90 ? 'text-green-700' : score >= 70 ? 'text-amber-600' : 'text-red-700';

/** Tiny trend line for a tile (T-35); decorative, the tile text says the state. */
export function Sparkline({ values }: { values: readonly number[] }) {
  if (values.length < 2) return null;
  const w = 120;
  const h = 28;
  const min = Math.min(...values, 50);
  const max = 100;
  const x = (i: number) => (i / (values.length - 1)) * (w - 4) + 2;
  const y = (v: number) => h - 2 - ((v - min) / (max - min || 1)) * (h - 4);
  const last = values[values.length - 1] ?? 0;
  return (
    <svg
      aria-hidden="true"
      viewBox={`0 0 ${w} ${h}`}
      className={`h-7 w-30 ${tone(last)}`}
      preserveAspectRatio="none"
    >
      <polyline
        points={values.map((v, i) => `${x(i)},${y(v)}`).join(' ')}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <circle cx={x(values.length - 1)} cy={y(last)} r="2.5" fill="currentColor" />
    </svg>
  );
}

/** Half-circle gauge with the score of an area. */
export function Gauge({ score, label }: { score: number; label: string }) {
  const r = 52;
  const length = Math.PI * r;
  const filled = (Math.max(0, Math.min(100, score)) / 100) * length;
  return (
    <figure className="flex flex-col items-center">
      <svg viewBox="0 0 128 72" className={`w-40 ${tone(score)}`} role="img" aria-label={label}>
        <path
          d="M 12 66 A 52 52 0 0 1 116 66"
          fill="none"
          stroke="currentColor"
          strokeOpacity="0.15"
          strokeWidth="12"
          strokeLinecap="round"
        />
        <path
          d="M 12 66 A 52 52 0 0 1 116 66"
          fill="none"
          stroke="currentColor"
          strokeWidth="12"
          strokeLinecap="round"
          strokeDasharray={`${filled} ${length}`}
        />
        <text
          x="64"
          y="62"
          textAnchor="middle"
          className="fill-slate-900 text-[28px] font-bold"
          aria-hidden="true"
        >
          {score}
        </text>
      </svg>
    </figure>
  );
}

/** Area score of the last 30 days, one point per day (the history the Health Score keeps). */
export function TrendChart({ points }: { points: readonly { day: string; score: number }[] }) {
  const { t, i18n } = useTranslation();
  if (points.length < 2) return <p className="text-slate-700">{t('area.trendEmpty')}</p>;
  const w = 600;
  const h = 160;
  const pad = { l: 32, r: 8, t: 8, b: 24 };
  const x = (i: number) => pad.l + (i / (points.length - 1)) * (w - pad.l - pad.r);
  const y = (v: number) => pad.t + (1 - v / 100) * (h - pad.t - pad.b);
  const day = (d: string) => formatDate(`${d}T12:00:00Z`, i18n.language, 'UTC');
  const first = points[0];
  const last = points[points.length - 1];
  return (
    <figure>
      <svg
        viewBox={`0 0 ${w} ${h}`}
        className="h-auto w-full text-blue-700"
        role="img"
        aria-label={t('area.trendDescription', {
          from: first ? day(first.day) : '',
          to: last ? day(last.day) : '',
          start: first?.score ?? 0,
          end: last?.score ?? 0,
        })}
      >
        {[0, 50, 100].map((v) => (
          <g key={v}>
            <line
              x1={pad.l}
              x2={w - pad.r}
              y1={y(v)}
              y2={y(v)}
              className="stroke-slate-200"
              strokeWidth="1"
            />
            <text
              x={pad.l - 6}
              y={y(v) + 4}
              textAnchor="end"
              className="fill-slate-600 text-[11px]"
            >
              {v}
            </text>
          </g>
        ))}
        <polyline
          points={points.map((p, i) => `${x(i)},${y(p.score)}`).join(' ')}
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinejoin="round"
        />
        {first ? (
          <text x={pad.l} y={h - 6} className="fill-slate-600 text-[11px]">
            {day(first.day)}
          </text>
        ) : null}
        {last ? (
          <text x={w - pad.r} y={h - 6} textAnchor="end" className="fill-slate-600 text-[11px]">
            {day(last.day)}
          </text>
        ) : null}
      </svg>
    </figure>
  );
}

/** "Now → after the fix" as two bars on the same 0–100 scale. */
export function BeforeAfter({ now, after }: { now: number; after: number }) {
  const { t } = useTranslation();
  const bar = (label: string, value: number, color: string) => (
    <div className="flex items-center gap-2">
      <span className="w-32 shrink-0 text-sm text-slate-700">{label}</span>
      <span className="h-3 flex-1 rounded bg-slate-100">
        <span
          className={`block h-3 rounded ${color}`}
          style={{ width: `${Math.max(2, Math.min(100, value))}%` }}
        />
      </span>
      <span className="w-10 text-right text-sm font-semibold tabular-nums">{value}</span>
    </div>
  );
  return (
    <div className="flex flex-col gap-1" aria-label={t('area.barsSr', { now, after })} role="img">
      {bar(t('area.before'), now, 'bg-slate-500')}
      {bar(t('area.after'), after, 'bg-green-600')}
    </div>
  );
}
